import { Database, FileMagnifyingGlass, Flag, IdentificationCard } from "@phosphor-icons/react"
import { useNavigate } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { useSessionStorage } from "usehooks-ts"

import EntityListSidebar from "~/components/customer-management/EntityListSidebar"
import { OverviewGrid, OverviewPage } from "~/components/design-system/pages"
import { OrganizationFeatureFlags } from "~/features/feature-flags/pages"
import { Button } from "~/components/reusable/Button"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { CompanyMetaCard } from "~/components/reusable/organizations/CompanyMetaCard"
import { EnabledOrgConnectionsCard } from "~/components/reusable/organizations/EnabledOrgConnectionsCard"
import { OrgMetaDataCard } from "~/components/reusable/organizations/OrgMetaDataCard"
import { TabsList, TabsRoot, TabsTrigger } from "~/components/reusable/Tabs"
import { InvitationsCard } from "~/components/settings/InvitationsCard"
import { UserManagementCard } from "~/components/settings/UserManagementCard"
import { OrgIngestionsTab } from "~/components/status/OrgIngestionTab"
import { OrgViewsTab } from "~/components/status/OrgViewsTab"
import { type Organization } from "~/server/api/routers/auth0"
import { api } from "~/utils/api"
import { useAuth0 } from "@auth0/auth0-react"
import { useUser } from "~/providers/userProvider"
import OrgsDataOverview from "../../components/reusable/organizations/OrgsDataOverview"

const TABS = {
  metadata: { label: "Metadata", icon: <IdentificationCard /> },
  ingestion: { label: "Ingestion", icon: <Database /> },
  "bq-views": { label: "BQ Views", icon: <FileMagnifyingGlass /> },
  "feature-flags": { label: "Feature flags", icon: <Flag /> },
}

export type OrganizationTab = keyof typeof TABS

/** The `?tab=` values, which are also the tab ids. */
export const ORGANIZATION_TABS = Object.keys(TABS) as [OrganizationTab, ...OrganizationTab[]]

const isOrganizationTab = (value: string): value is OrganizationTab => Object.hasOwn(TABS, value)

const filterOrganizations = (organizations: Organization[] | undefined, searchText: string) => {
  if (!organizations) return []
  const needle = searchText.trim().toLowerCase()
  if (!needle) return organizations
  return organizations.filter(
    (organization) =>
      organization.display_name.toLowerCase().includes(needle) ||
      organization.name.toLowerCase().includes(needle),
  )
}

type OrganizationsProps = {
  selectedOrgIdFromSearch?: string
  /** The open tab; without it, the last one used in this browser tab. */
  tabFromSearch?: OrganizationTab
}

function Organizations({ selectedOrgIdFromSearch, tabFromSearch }: OrganizationsProps) {
  const { user, isLoading, isAuthenticated } = useAuth0()
  const { permissions } = useUser()

  const navigate = useNavigate({ from: "/internal/customer-management/organizations" })
  const [storedTab, setStoredTab] = useSessionStorage<string>("organizations-tab", "metadata")
  const tab = tabFromSearch ?? (isOrganizationTab(storedTab) ? storedTab : "metadata")
  const selectTab = (next: OrganizationTab) => {
    setStoredTab(next)
    void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
  }

  const { data: organizations, isLoading: isLoadingOrganizations } =
    api.auth0.getAllOrganizations.useQuery()

  const [searchText, setSearchText] = useState<string>("")
  /** `undefined` means nothing picked yet, `null` is the "All" view, a string is a chosen org. */
  const [selectedOrgId, setSelectedOrgId] = useState<string | null | undefined>(undefined)

  const filteredOrganizations = useMemo(
    () => filterOrganizations(organizations, searchText),
    [organizations, searchText],
  )

  const selectedOrganization = useMemo(() => {
    if (!organizations?.length) return null
    if (selectedOrgId !== undefined) {
      return selectedOrgId === null
        ? null
        : (organizations.find((organization) => organization.id === selectedOrgId) ?? null)
    }
    const deepLinked = selectedOrgIdFromSearch
      ? organizations.find((organization) => organization.id === selectedOrgIdFromSearch)
      : undefined
    return (
      deepLinked ?? organizations.find((organization) => organization.id === user?.org_id) ?? null
    )
  }, [organizations, selectedOrgId, selectedOrgIdFromSearch, user?.org_id])

  /** A search that narrows to exactly one organization selects it. */
  const handleSearchTextChange = (text: string) => {
    setSearchText(text)
    const matches = filterOrganizations(organizations, text)
    if (matches.length === 1 && matches[0]) {
      setSelectedOrgId(matches[0].id)
    }
  }

  if (!user && isLoading) {
    return <LoadingOverlay />
  }

  if (!isAuthenticated) {
    return <div className="bg-subtle p-4">Not authenticated</div>
  }

  if (!permissions.isStaff) {
    return <div className="bg-subtle p-4">User not allowed!</div>
  }

  return (
    <OverviewPage fillContent contentClassName="p-0">
      <div className="flex min-h-0 flex-1">
        <EntityListSidebar
          title="Organizations"
          searchPlaceholder="Search organizations"
          searchText={searchText}
          onSearchTextChange={handleSearchTextChange}
          isLoading={isLoadingOrganizations}
          isEmpty={filteredOrganizations.length === 0}
          emptyMessage="No organizations found"
          collapseStorageKey="customer-management.organizations-sidebar-collapsed"
        >
          <Button
            key="all"
            variant={selectedOrganization === null ? "primary" : "neutral"}
            onClick={() => setSelectedOrgId(null)}
          >
            All
          </Button>

          {filteredOrganizations.map((organization) => (
            <Button
              key={organization.id}
              variant={organization.id === selectedOrganization?.id ? "primary" : "neutral"}
              onClick={() => setSelectedOrgId(organization.id)}
            >
              {organization.display_name}
            </Button>
          ))}
        </EntityListSidebar>
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
          {!selectedOrganization ? (
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <OrgsDataOverview />
            </div>
          ) : (
            <>
              <div className="shrink-0 px-4 pt-4">
                <TabsRoot
                  value={tab}
                  onValueChange={(value) => isOrganizationTab(value) && selectTab(value)}
                >
                  <TabsList>
                    {Object.entries(TABS).map(([id, { label, icon }]) => (
                      <TabsTrigger key={id} value={id} className="gap-1.5 px-2">
                        {icon}
                        {label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </TabsRoot>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {tab === "bq-views" && <OrgViewsTab orgId={selectedOrganization.name} />}
                {tab === "ingestion" && <OrgIngestionsTab orgId={selectedOrganization.name} />}
                {tab === "feature-flags" && (
                  <OrganizationFeatureFlags orgId={selectedOrganization.id} />
                )}
                {tab === "metadata" && (
                  <OverviewGrid className="items-start xl:grid-cols-2">
                    <div className="flex flex-col gap-4">
                      <OrgMetaDataCard
                        organizationId={selectedOrganization.id}
                        onOrganizationDeleted={() => {
                          setSelectedOrgId(null)
                        }}
                      />
                      <EnabledOrgConnectionsCard organizationId={selectedOrganization.id} />
                      <CompanyMetaCard organizationName={selectedOrganization.name} />
                    </div>
                    <div className="flex flex-col gap-4">
                      <InvitationsCard
                        organizationId={selectedOrganization.id}
                        className="max-h-[400px]"
                      />
                      <UserManagementCard
                        organizationId={selectedOrganization.id}
                        organizationName={selectedOrganization.name}
                        className="h-fit max-h-[640px]"
                      />
                    </div>
                  </OverviewGrid>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </OverviewPage>
  )
}

export default Organizations
